import React from 'react';
import { Input, Alert, Card, Icon } from '@amigoapp/doca-react';
import { Loading, Container, Text, Button, AdvancedSelect, EmptyState, Avatar } from '../../components';
import FileUpload from '../../components/FileUpload';
import { MAX_PATIENT_DOCUMENT_FILE } from '../../constants/patientFiles';
import { formatCpf, formatDate, formatPhone, formatZipcode, formatMonthYear } from '../../utils/formatter';
import { CompanyHeader, CompanyDivider, SectionHeader, FieldRow } from './PatientUpdateRegisterForm.styles';

const PAYMENT_METHODS = {
	CONVENIO: 'CONVENIO',
	PARTICULAR: 'PARTICULAR'
};

function PatientUpdateRegisterForm({
	loading,
	linkExpired,
	submitted,
	values,
	variant,
	companyName,
	companyLogoUrl,
	canSubmit,
	pendingCount,
	isFieldPending,
	insuranceOptions,
	paymentMethod,
	insuranceCard,
	onChange,
	onChangeWithMask,
	onSelectInsurance,
	onSubmitClick,
	onTogglePaymentMethod
}) {
	if (loading) {
		return <Loading />;
	}

	if (linkExpired) {
		return (
			<EmptyState
				icon="link-slash"
				title="Link inválido ou expirado"
				description="Solicite um novo link para atualizar seus dados cadastrais."
			/>
		);
	}

	if (submitted) {
		return (
			<EmptyState
				icon="circle-check"
				title="Dados atualizados com sucesso!"
				description="Suas informações foram registradas."
			/>
		);
	}

	return (
		<Container padded>
			{companyName && (
				<CompanyHeader>
					{companyLogoUrl && (
						<>
							<Avatar url={companyLogoUrl} size="small" rounded />
							<CompanyDivider />
						</>
					)}
					<Text weight="semibold">{companyName}</Text>
				</CompanyHeader>
			)}

			<Text size="large" weight="semibold" marginBottom={8}>
				{values?.name ? `Olá, ${values.name}` : 'Olá!'}
			</Text>

			<Text variant="gray" marginBottom={16}>
				Confirme se os dados abaixo estão corretos. Leva cerca de dois minutos.
			</Text>

			{pendingCount > 0 && (
				<Alert variant="info" icon="circle-info" className="doca-mb-4">
					{pendingCount} {pendingCount === 1 ? 'campo precisa' : 'campos precisam'} da sua atenção.
				</Alert>
			)}

			<form onSubmit={onSubmitClick}>
				<Card className="doca-mb-4">
					<Card.Content>
						<SectionHeader>
							<Icon name="user" />
							<Text weight="semibold">Dados pessoais</Text>
						</SectionHeader>

						<Input
							label="Nome completo"
							name="name"
							className="doca-mb-4"
							value={values?.name}
							onChange={onChange}
						/>

						<FieldRow className="doca-mb-4">
							<Input
								label="CPF"
								name="cpf"
								disabled
								placeholder="Não informado"
								value={values?.cpf ? formatCpf(values.cpf) : ''}
							/>

							<Input
								label="Nascimento"
								name="born"
								type="tel"
								value={values?.born}
								onChange={event => onChangeWithMask(event, formatDate)}
							/>
						</FieldRow>

						<Input
							label="Nome social (opcional)"
							name="social_name"
							placeholder="Como prefere ser chamado"
							value={values?.social_name}
							onChange={onChange}
						/>
					</Card.Content>
				</Card>

				<Card className="doca-mb-4">
					<Card.Content>
						<SectionHeader>
							<Icon name="phone" />
							<Text weight="semibold">Contato</Text>
						</SectionHeader>

						<div className="doca-form__field doca-mb-4">
							<Input
								label="Celular"
								name="contact_cellphone"
								type="tel"
								maxLength={15}
								value={values?.contact_cellphone}
								onChange={event => onChangeWithMask(event, formatPhone)}
							/>
							<Text size="small" variant="gray" marginTop={4}>Usamos este número para lembretes de consulta.</Text>
						</div>

						<Input
							label="E-mail"
							name="email"
							type="email"
							value={values?.email}
							onChange={onChange}
						/>
					</Card.Content>
				</Card>

				<Card className="doca-mb-4">
					<Card.Content>
						<SectionHeader>
							<Icon name="location-dot" />
							<Text weight="semibold">Endereço</Text>
						</SectionHeader>

						<FieldRow className="doca-mb-4">
							<Input
								label="CEP"
								name="address_cep"
								type="tel"
								maxLength={9}
								value={values?.address_cep}
								onChange={event => onChangeWithMask(event, formatZipcode)}
							/>

							<Input
								label="Número"
								name="address_number"
								value={values?.address_number}
								onChange={onChange}
							/>
						</FieldRow>

						<Input
							label="Logradouro"
							name="address_address"
							className="doca-mb-4"
							value={values?.address_address}
							onChange={onChange}
						/>

						<Input
							label="Complemento (opcional)"
							name="address_complement"
							placeholder="Apartamento, bloco, referência"
							value={values?.address_complement}
							onChange={onChange}
						/>
					</Card.Content>
				</Card>

				<Card className="doca-mb-4">
					<Card.Content>
						<SectionHeader>
							<Icon name="id-card" />
							<Text weight="semibold">Convênio</Text>
						</SectionHeader>

						<div className="doca-form__field doca-form__field--required doca-mb-4">
							<label className="doca-form__label">Forma de atendimento</label>
							<div className="doca-shortcut__container d-block">
								<button
									type="button"
									className={`doca-shortcut w-100 ${paymentMethod === PAYMENT_METHODS.CONVENIO ? `doca-shortcut--${variant}` : ''}`}
									onClick={() => onTogglePaymentMethod(PAYMENT_METHODS.CONVENIO)}
								>
									<div>
										<i className="doca-icon doca-icon--large doca-icon__hospital" />
										<p className="doca-shortcut__title">Convênio</p>
									</div>
								</button>

								<button
									type="button"
									className={`doca-shortcut w-100 ${paymentMethod === PAYMENT_METHODS.PARTICULAR ? `doca-shortcut--${variant}` : ''}`}
									onClick={() => onTogglePaymentMethod(PAYMENT_METHODS.PARTICULAR)}
								>
									<div>
										<i className="doca-icon doca-icon--large doca-icon__credit-card" />
										<p className="doca-shortcut__title">Particular</p>
									</div>
								</button>
							</div>
						</div>

						{paymentMethod === PAYMENT_METHODS.CONVENIO && (
							<>
								<div className="doca-mb-4">
									<AdvancedSelect
										value={values?.insurance_id || ''}
										label="Operadora"
										placeholder="Selecione"
										onChange={onSelectInsurance}
										options={[
											{ label: 'Selecione', value: '' },
											...insuranceOptions.map(insurance => ({
												label: insurance.name,
												value: insurance.id,
												key: insurance.id
											}))
										]}
									/>
								</div>

								<FieldRow>
									<Input
										label="Número da carteirinha"
										name="insurance_number"
										placeholder="Somente números"
										value={values?.insurance_number}
										onChange={onChange}
									/>

									<Input
										label="Validade"
										name="insurance_validate"
										type="tel"
										placeholder="MM/AAAA"
										value={values?.insurance_validate}
										onChange={event => onChangeWithMask(event, formatMonthYear)}
									/>
								</FieldRow>

								{(isFieldPending('insurance_number') || isFieldPending('insurance_validate')) && (
									<div className="doca-form__feedback doca-form__feedback--error">
										Campo obrigatório para atendimento por convênio.
									</div>
								)}

								<div className="doca-w-full doca-mt-4">
									<label className="doca-form__label">Foto da carteirinha</label>
									<FileUpload
										variant="card"
										colorVariant={variant}
										maxFiles={MAX_PATIENT_DOCUMENT_FILE}
										label="Enviar foto da carteirinha"
										selectedFiles={insuranceCard?.files}
										loading={insuranceCard?.loading}
										onFileSelect={insuranceCard?.onSelect}
										onRemoveFile={insuranceCard?.onRemove}
									/>
								</div>
							</>
						)}
					</Card.Content>
				</Card>

				<div className="doca-checkbox__container doca-my-4">
					<input
						id="confirm-registration-data"
						className="doca-checkbox"
						type="checkbox"
						name="confirm"
						onChange={onChange}
						checked={!!values?.confirm}
					/>

					<label htmlFor="confirm-registration-data" className="doca-form__label doca-form__label--inline">
						Confirmo que os dados acima são verdadeiros e autorizo o uso deles para meu atendimento, conforme a Política de Privacidade.
					</label>
				</div>

				<Button
					block
					size="large"
					disabled={!canSubmit}
					variant={variant}
					type="submit"
					marginBottom={16}
				>
					Salvar atualização
				</Button>

				<Text size="small" variant="gray" textAlign="center">
					Você pode atualizar seus dados novamente na recepção.
				</Text>
			</form>
		</Container>
	);
}

export default PatientUpdateRegisterForm;
